import { Center, Select as ChakraSelect, SelectProps, Spinner } from '@chakra-ui/react';
import * as React from 'react';

import { IOption } from '../../../types';
import { CustomFormControl, DirectionProp } from '../CustomFormControl';
import { withConfirm } from '../withConfirm';
import { UndoEdit } from '../../iconButtons/UndoEdit';

interface IDropdownOption {
  value?: string;
  label: string;
  id?: string;
}

interface IProps extends DirectionProp {
  value?: string | number;
  id?: string;
  name?: string;
  placeHolder?: string;
  options?: IOption[];
  label?: string;
  subText?: string;
  inputHelperText?: string;
  helperText?: React.ReactNode;
  errorText?: string;
  forwardedRef?: ((instance: HTMLSelectElement | null) => void) | React.RefObject<HTMLSelectElement> | null | undefined;
  onChange?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onBlur?: (event: React.FormEvent<HTMLSelectElement>) => void;
  onFocus?: (event: React.FormEvent<HTMLSelectElement>) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  size?: 'lg' | 'md' | 'sm';
  mb?: number | string;
  mt?: number | string;
  selectProps?: SelectProps;
  canUndo?: boolean;
  undoSize?: 'lg' | 'md' | 'sm';
  onUndo?: () => void;
}

const Select: React.FC<IProps> = (props: IProps) => {
  const {
    value,
    id,
    name,
    placeHolder = 'Please choose an option…',
    canUndo = false,
    onUndo = () => {},
    undoSize,
    options,
    label,
    subText,
    errorText,
    forwardedRef,
    onChange,
    onBlur,
    onFocus,
    isDisabled,
    isLoading,
    helperText,
    size = 'lg',
    mb = 0,
    mt = 0,
    direction,
    selectProps
  } = props;

  const renderOptions = () => {
    if (!options) {
      return [];
    }

    const renderedOptions = [];

    options.forEach((option, key) => {
      if (option.populate) {
        renderedOptions.push(
          <optgroup label={option.label} key={key}>
            {option.populate.options.map((option, index) => {
              return (
                <option key={`${key}-${index}`} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </optgroup>
        );
      } else {
        renderedOptions.push(
          <option key={key} value={option.value}>
            {option.label}
          </option>
        );
      }
    });

    return renderedOptions;
  };

  const onSelect = React.useCallback(
    (event: React.FormEvent<HTMLSelectElement>) => {
      // ignore placeholder option
      if (onChange && event.currentTarget.value !== '') onChange(event);
    },
    [onChange]
  );

  return (
    <CustomFormControl
      id={id}
      controlProps={{
        isInvalid: errorText ? true : false,
        mb: mb,
        mt: mt
      }}
      direction={direction}
      size={size}
      label={label}
      labelProps={{ htmlFor: `${id}-input-field` }}
      subLabel={subText}
      helperText={helperText}
      error={errorText}
    >
      {isLoading ? (
        <ChakraSelect name={name} as={Center} size={size} isDisabled>
          <Spinner />
        </ChakraSelect>
      ) : (
        <ChakraSelect
          id={`${id}-input-field`}
          name={name}
          data-selector={`${id}-input-field`}
          aria-label={label ? null : label}
          onChange={onSelect}
          value={value}
          ref={forwardedRef}
          isInvalid={!!errorText}
          onBlur={onBlur}
          onFocus={onFocus}
          size={size}
          placeholder={placeHolder}
          isDisabled={isDisabled}
          {...selectProps}
        >
          {renderOptions()}
        </ChakraSelect>
      )}
      {canUndo ? <UndoEdit id={id} onUndo={onUndo} size={undoSize} /> : null}
    </CustomFormControl>
  );
};

const SelectWithConfirm = withConfirm(Select);

export { Select, SelectWithConfirm };
export type { IDropdownOption };
